import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './PropertyManagersPage.css';

const API_BASE = process.env.REACT_APP_API_BASE;

function getToken() {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
}

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function validatePassword(pw) {
  if (!pw || pw.length < 8)         return 'At least 8 characters required.';
  if (!/[A-Z]/.test(pw))            return 'Must contain an uppercase letter.';
  if (!/[a-z]/.test(pw))            return 'Must contain a lowercase letter.';
  if (!/\d/.test(pw))               return 'Must contain a number.';
  return '';
}

const EMPTY_ADD  = { name: '', email: '', phone: '', address: '' };
const EMPTY_PASS = { password: '', confirmPassword: '' };

// ── Icons ─────────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
      <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}
function EditIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
      <path d="M13 3l4 4L6 18H2v-4L13 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}
function KeyIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
      <circle cx="8" cy="11" r="4" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12 7l5 5M15 4l1 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function PowerIcon({ active }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="14" height="14">
      <path d="M10 3v5" stroke={active ? '#16a34a' : '#C8102E'} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M6.3 5.3A7 7 0 1013.7 5.3" stroke={active ? '#16a34a' : '#C8102E'} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
      <circle cx="8.5" cy="8.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M13 13l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function EyeIcon({ show }) {
  return show ? (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
      <path d="M3 3l14 14M12.45 12.45A3 3 0 017.55 7.55M8.7 5.1A8.7 8.7 0 0110 5c5 0 8 5 8 5a15.3 15.3 0 01-2.7 3.3M6.5 6.5A14.5 14.5 0 002 10s3 5 8 5a8.6 8.6 0 004.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
      <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, width = 480 }) {
  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div
        className="pm-modal"
        style={{ maxWidth: width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pm-modal-head">
          <h3>{title}</h3>
          <button className="pm-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Password field ─────────────────────────────────────────────────────────────

function PasswordField({ label, value, onChange, error, placeholder = '••••••••' }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pm-field">
      <label className="pm-label">{label}</label>
      <div className="pm-input-wrap">
        <input
          type={show ? 'text' : 'password'}
          className={`pm-input ${error ? 'pm-input--err' : ''}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="new-password"
        />
        <button type="button" className="pm-eye" onClick={() => setShow(v => !v)}>
          <EyeIcon show={show} />
        </button>
      </div>
      {error && <span className="pm-field-err">{error}</span>}
    </div>
  );
}

// ── Add Manager Modal ─────────────────────────────────────────────────────────

function AddManagerModal({ onClose, onSuccess }) {
  const [form, setForm]       = useState(EMPTY_ADD);
  const [errors, setErrors]   = useState({});
  const [submitting, setSub]  = useState(false);
  const [apiErr, setApiErr]   = useState('');

  function set(key) { return (e) => setForm(f => ({ ...f, [key]: e.target.value })); }

  function validate() {
    const e = {};
    if (!form.name.trim())  e.name  = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email.';
    if (!form.phone.trim()) e.phone = 'Phone is required.';
    else if (!/^\d{10,15}$/.test(form.phone.replace(/[\s\-()+]/g, ''))) e.phone = 'Enter a valid phone (10–15 digits).';
    return e;
  }

  async function submit(e) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSub(true);
    setApiErr('');
    try {
      const res = await fetch(`${API_BASE}/api/managers`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setApiErr(data.detail || 'Registration failed.'); return; }
      onSuccess('Manager registered successfully.');
    } catch {
      setApiErr('Connection error. Please try again.');
    } finally {
      setSub(false);
    }
  }

  return (
    <Modal title="Add Property Manager" onClose={onClose} width={540}>
      <form className="pm-modal-body" onSubmit={submit} noValidate>
        {apiErr && <div className="pm-api-err">{apiErr}</div>}

        <div className="pm-row-2">
          <div className="pm-field">
            <label className="pm-label">Manager Name <span className="pm-req">*</span></label>
            <input className={`pm-input ${errors.name ? 'pm-input--err' : ''}`} value={form.name} onChange={set('name')} placeholder="Jane Smith" />
            {errors.name && <span className="pm-field-err">{errors.name}</span>}
          </div>
          <div className="pm-field">
            <label className="pm-label">Email <span className="pm-req">*</span></label>
            <input type="email" className={`pm-input ${errors.email ? 'pm-input--err' : ''}`} value={form.email} onChange={set('email')} placeholder="jane@example.com" />
            {errors.email && <span className="pm-field-err">{errors.email}</span>}
          </div>
        </div>

        <div className="pm-row-2">
          <div className="pm-field">
            <label className="pm-label">Phone <span className="pm-req">*</span></label>
            <input className={`pm-input ${errors.phone ? 'pm-input--err' : ''}`} value={form.phone} onChange={set('phone')} placeholder="+1 234 567 8900" />
            {errors.phone && <span className="pm-field-err">{errors.phone}</span>}
          </div>
          <div className="pm-field">
            <label className="pm-label">Contact Address</label>
            <input className="pm-input" value={form.address} onChange={set('address')} placeholder="123 Main St, City" />
          </div>
        </div>

        <p className="pm-hint" style={{ marginTop: 4 }}>
          A temporary password will be generated and emailed to the manager.
        </p>

        <div className="pm-modal-foot">
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="pm-btn pm-btn-primary" disabled={submitting}>
            {submitting ? <span className="pm-spinner" /> : 'Register Manager'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit Manager Modal ────────────────────────────────────────────────────────

function EditManagerModal({ manager, onClose, onSuccess }) {
  const [form, setForm]      = useState({ name: manager.manager_name, phone: manager.phone, address: manager.contact_address || '' });
  const [errors, setErrors]  = useState({});
  const [submitting, setSub] = useState(false);
  const [apiErr, setApiErr]  = useState('');

  function set(key) { return (e) => setForm(f => ({ ...f, [key]: e.target.value })); }

  async function submit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (!form.phone.trim()) errs.phone = 'Phone is required.';
    else if (!/^\d{10,15}$/.test(form.phone.replace(/[\s\-()+]/g, ''))) errs.phone = 'Enter a valid phone.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSub(true);
    setApiErr('');
    try {
      const res = await fetch(`${API_BASE}/api/managers/${manager.manager_id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ name: form.name.trim(), phone: form.phone.trim(), address: form.address.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setApiErr(data.detail || 'Update failed.'); return; }
      onSuccess('Manager details updated.');
    } catch {
      setApiErr('Connection error. Please try again.');
    } finally {
      setSub(false);
    }
  }

  return (
    <Modal title="Edit Manager" onClose={onClose}>
      <form className="pm-modal-body" onSubmit={submit} noValidate>
        {apiErr && <div className="pm-api-err">{apiErr}</div>}

        <div className="pm-field pm-field--readonly">
          <label className="pm-label">Email (login — cannot change)</label>
          <input className="pm-input" value={manager.email} readOnly />
        </div>

        <div className="pm-field">
          <label className="pm-label">Manager Name <span className="pm-req">*</span></label>
          <input className={`pm-input ${errors.name ? 'pm-input--err' : ''}`} value={form.name} onChange={set('name')} />
          {errors.name && <span className="pm-field-err">{errors.name}</span>}
        </div>

        <div className="pm-row-2">
          <div className="pm-field">
            <label className="pm-label">Phone <span className="pm-req">*</span></label>
            <input className={`pm-input ${errors.phone ? 'pm-input--err' : ''}`} value={form.phone} onChange={set('phone')} />
            {errors.phone && <span className="pm-field-err">{errors.phone}</span>}
          </div>
          <div className="pm-field">
            <label className="pm-label">Contact Address</label>
            <input className="pm-input" value={form.address} onChange={set('address')} />
          </div>
        </div>

        <div className="pm-modal-foot">
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="pm-btn pm-btn-primary" disabled={submitting}>
            {submitting ? <span className="pm-spinner" /> : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Reset Password Modal ──────────────────────────────────────────────────────

function ResetPasswordModal({ manager, onClose, onSuccess }) {
  const [form, setForm]      = useState(EMPTY_PASS);
  const [errors, setErrors]  = useState({});
  const [submitting, setSub] = useState(false);
  const [apiErr, setApiErr]  = useState('');

  function set(key) { return (e) => setForm(f => ({ ...f, [key]: e.target.value })); }

  async function submit(e) {
    e.preventDefault();
    const errs = {};
    const pwErr = validatePassword(form.password);
    if (pwErr) errs.password = pwErr;
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSub(true);
    setApiErr('');
    try {
      const res = await fetch(`${API_BASE}/api/managers/${manager.manager_id}/reset-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) { setApiErr(data.detail || 'Reset failed.'); return; }
      onSuccess('Password reset successfully.');
    } catch {
      setApiErr('Connection error. Please try again.');
    } finally {
      setSub(false);
    }
  }

  return (
    <Modal title={`Reset Password — ${manager.manager_name}`} onClose={onClose}>
      <form className="pm-modal-body" onSubmit={submit} noValidate>
        {apiErr && <div className="pm-api-err">{apiErr}</div>}
        <p className="pm-hint" style={{ marginBottom: 16 }}>
          This will immediately change the login password for <strong>{manager.email}</strong>.
        </p>

        <PasswordField label={<>New Password <span className="pm-req">*</span></>} value={form.password} onChange={set('password')} error={errors.password} />
        <PasswordField label={<>Confirm Password <span className="pm-req">*</span></>} value={form.confirmPassword} onChange={set('confirmPassword')} error={errors.confirmPassword} />
        <p className="pm-hint">8+ chars · uppercase · lowercase · number</p>

        <div className="pm-modal-foot">
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="pm-btn pm-btn-danger" disabled={submitting}>
            {submitting ? <span className="pm-spinner" /> : 'Reset Password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Confirm Activate Modal ────────────────────────────────────────────────────

function ConfirmActivateModal({ manager, onClose, onConfirm, submitting }) {
  return (
    <Modal title="Activate Manager" onClose={onClose} width={400}>
      <div className="pm-modal-body">
        <p className="pm-confirm-text">
          Activate <strong>{manager.manager_name}</strong>? They will be able to log in immediately.
        </p>
        <div className="pm-modal-foot">
          <button className="pm-btn pm-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="pm-btn pm-btn-success" onClick={onConfirm} disabled={submitting}>
            {submitting ? <span className="pm-spinner" /> : 'Activate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Deactivate Modal (with property transfer) ─────────────────────────────────

function DeactivateModal({ manager, activeManagers, onClose, onConfirm, submitting }) {
  const [transferTo, setTransferTo] = useState('');
  const others = activeManagers.filter(m => m.manager_id !== manager.manager_id);

  return (
    <Modal title="Deactivate Manager" onClose={onClose} width={460}>
      <div className="pm-modal-body">
        <p className="pm-confirm-text">
          Deactivate <strong>{manager.manager_name}</strong>? They will not be able to log in until reactivated.
        </p>

        <div className="pm-field" style={{ marginTop: 16 }}>
          <label className="pm-label">Transfer Properties To</label>
          <select
            className="pm-input pm-select"
            value={transferTo}
            onChange={e => setTransferTo(e.target.value)}
          >
            <option value="">— No transfer —</option>
            {others.map(m => (
              <option key={m.manager_id} value={m.manager_id}>
                {m.manager_name} ({m.email})
              </option>
            ))}
          </select>
          <p className="pm-hint" style={{ marginTop: 5 }}>
            All properties assigned to this manager will be transferred to the selected manager.
          </p>
        </div>

        <div className="pm-modal-foot">
          <button className="pm-btn pm-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="pm-btn pm-btn-danger"
            onClick={() => onConfirm(transferTo || null)}
            disabled={submitting}
          >
            {submitting ? <span className="pm-spinner" /> : 'Deactivate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className="pm-toast">{message}</div>;
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PropertyManagersPage() {
  const [managers, setManagers]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [modal, setModal]             = useState(null);   // 'add'|'edit'|'reset'|'toggle'
  const [selected, setSelected]       = useState(null);
  const [toggling, setToggling]       = useState(false);
  const [toast, setToast]             = useState('');

  const fetchManagers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/managers`, { headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).detail);
      setManagers(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load managers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchManagers(); }, [fetchManagers]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return managers.filter(m =>
      m.manager_name.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      (m.phone || '').includes(q)
    );
  }, [managers, search]);

  const stats = useMemo(() => ({
    total:    managers.length,
    active:   managers.filter(m => m.is_active && !m.must_reset_password).length,
    pending:  managers.filter(m => m.must_reset_password).length,
    inactive: managers.filter(m => !m.is_active).length,
  }), [managers]);

  function closeModal() { setModal(null); setSelected(null); }

  function handleSuccess(msg) {
    closeModal();
    setToast(msg);
    fetchManagers();
  }

  async function confirmToggle(transferToManagerId = null) {
    setToggling(true);
    try {
      const res = await fetch(`${API_BASE}/api/managers/${selected.manager_id}/toggle-status`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ transfer_to_manager_id: transferToManagerId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      handleSuccess(data.message);
    } catch (e) {
      setToast(e.message || 'Failed to update status.');
      closeModal();
    } finally {
      setToggling(false);
    }
  }

  return (
    <div className="pm-page">

      {/* ── Header ── */}
      <div className="pm-page-head">
        <div className="pm-summary">
          <span className="pm-chip pm-chip-total">Total <strong>{stats.total}</strong></span>
          <span className="pm-chip pm-chip-active">Active <strong>{stats.active}</strong></span>
          <span className="pm-chip pm-chip-pending">Pending <strong>{stats.pending}</strong></span>
          <span className="pm-chip pm-chip-inactive">Inactive <strong>{stats.inactive}</strong></span>
        </div>
        <button className="pm-add-btn" onClick={() => setModal('add')}>
          <PlusIcon /> Add Manager
        </button>
      </div>

      {/* ── Search ── */}
      <div className="pm-search-bar">
        <span className="pm-search-icon"><SearchIcon /></span>
        <input
          type="text"
          className="pm-search-input"
          placeholder="Search by name, email or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="pm-search-clear" onClick={() => setSearch('')}>
            <CloseIcon />
          </button>
        )}
      </div>

      {/* ── Error / loading ── */}
      {error && <div className="pm-alert">{error}</div>}

      {/* ── Table ── */}
      <div className="pm-table-card">
        {loading ? (
          <div className="pm-skeleton-rows">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="pm-skeleton-row">
                <div className="pm-sk" style={{ width: '30%' }} />
                <div className="pm-sk" style={{ width: '24%' }} />
                <div className="pm-sk" style={{ width: '14%' }} />
                <div className="pm-sk" style={{ width: '10%' }} />
                <div className="pm-sk" style={{ width: '14%' }} />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="pm-empty">
            {search ? `No managers match "${search}".` : 'No property managers registered yet.'}
          </div>
        ) : (
          <div className="pm-table-wrap">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Manager</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Registered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.manager_id}>
                    <td>
                      <div className="pm-manager-cell">
                        <div className="pm-avatar">{m.manager_name[0].toUpperCase()}</div>
                        <div>
                          <div className="pm-manager-name">{m.manager_name}</div>
                          <div className="pm-manager-email">{m.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="pm-td-phone">{m.phone || '—'}</td>
                    <td className="pm-td-addr">{m.contact_address || '—'}</td>
                    <td>
                      <span className={`pm-status-badge ${
                        m.must_reset_password ? 'pm-badge-pending'
                          : m.is_active       ? 'pm-badge-active'
                          :                     'pm-badge-inactive'
                      }`}>
                        {m.must_reset_password ? 'Pending Activation' : m.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="pm-td-date">{fmtDate(m.created_date)}</td>
                    <td>
                      <div className="pm-actions">
                        <button
                          className="pm-icon-btn pm-icon-edit"
                          title="Edit details"
                          onClick={() => { setSelected(m); setModal('edit'); }}
                        ><EditIcon /></button>
                        <button
                          className="pm-icon-btn pm-icon-key"
                          title="Reset password"
                          onClick={() => { setSelected(m); setModal('reset'); }}
                        ><KeyIcon /></button>
                        <button
                          className={`pm-icon-btn ${m.is_active ? 'pm-icon-deactivate' : 'pm-icon-activate'}`}
                          title={m.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => { setSelected(m); setModal('toggle'); }}
                        ><PowerIcon active={m.is_active} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="pm-table-foot">
            Showing {filtered.length} of {managers.length} manager{managers.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {modal === 'add'    && <AddManagerModal     onClose={closeModal} onSuccess={handleSuccess} />}
      {modal === 'edit'   && selected && <EditManagerModal    manager={selected} onClose={closeModal} onSuccess={handleSuccess} />}
      {modal === 'reset'  && selected && <ResetPasswordModal  manager={selected} onClose={closeModal} onSuccess={handleSuccess} />}
      {modal === 'toggle' && selected && (
        selected.is_active
          ? <DeactivateModal
              manager={selected}
              activeManagers={managers.filter(m => m.is_active)}
              onClose={closeModal}
              onConfirm={confirmToggle}
              submitting={toggling}
            />
          : <ConfirmActivateModal
              manager={selected}
              onClose={closeModal}
              onConfirm={() => confirmToggle(null)}
              submitting={toggling}
            />
      )}

      {/* ── Toast ── */}
      {toast && <Toast message={toast} onDone={() => setToast('')} />}
    </div>
  );
}
